import { useState, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical, ArrowRight } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FilesTab from '../components/FilesTab'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  Dash,
  InlineText,
  InlineNumber,
  InlineDate,
  InlineSelect,
  InlineStatus,
  RecordDrawer,
  useDeleteConfirm,
  RowActions,
} from '@/components/ui/inline-edit'
import { DatePicker } from '@/components/DatePicker'

// ─────────────────── Types ───────────────────
type Lead = {
  id: number
  first_name: string
  last_name: string
  full_name: string
  email: string
  phone: string
  job_title: string
  company_name: string
  company_size: '' | 'SOLO' | 'SMALL' | 'MEDIUM' | 'LARGE' | 'ENTERPRISE'
  industry: string
  website: string
  country_code: string
  source: 'WEBSITE' | 'REFERRAL' | 'COLD_CALL' | 'EVENT' | 'SOCIAL' | 'EMAIL_CAMPAIGN' | 'OTHER'
  status: 'NEW' | 'CONTACTED' | 'QUALIFIED' | 'UNQUALIFIED' | 'CONVERTED'
  score: number
  owner: number | null
  owner_username: string | null
  notes: string
  converted_account: number | null
  converted_account_name: string | null
  converted_contact: number | null
  converted_contact_name: string | null
  converted_opportunity: number | null
  converted_opportunity_name: string | null
  converted_at: string | null
}

type Activity = {
  id: number
  activity_type: 'CALL' | 'EMAIL' | 'MEETING' | 'NOTE' | 'TASK'
  subject: string
  description: string
  related_lead: number | null
  related_lead_name: string | null
  owner: number | null
  owner_username: string | null
  status: 'PENDING' | 'COMPLETED' | 'CANCELLED'
  due_date: string | null
  completed_at: string | null
  duration_minutes: number | null
  is_overdue: boolean
}

// ─────────────────── Constants ───────────────────
const STATUS_COLOR: Record<Lead['status'], string> = {
  NEW: 'bg-blue-100 text-blue-800',
  CONTACTED: 'bg-amber-100 text-amber-800',
  QUALIFIED: 'bg-purple-100 text-purple-800',
  UNQUALIFIED: 'bg-gray-100 text-gray-600',
  CONVERTED: 'bg-green-100 text-green-800',
}

const SOURCE_LABEL: Record<Lead['source'], string> = {
  WEBSITE: 'Website',
  REFERRAL: 'Referral',
  COLD_CALL: 'Cold call',
  EVENT: 'Event',
  SOCIAL: 'Social media',
  EMAIL_CAMPAIGN: 'Email campaign',
  OTHER: 'Other',
}

const SIZE_LABEL: Record<string, string> = {
  SOLO: '1 employee',
  SMALL: '2-50',
  MEDIUM: '51-250',
  LARGE: '251-1,000',
  ENTERPRISE: '1,000+',
}

const ACTIVITY_STATUS_COLOR: Record<Activity['status'], string> = {
  PENDING: 'bg-blue-100 text-blue-800',
  COMPLETED: 'bg-green-100 text-green-800',
  CANCELLED: 'bg-gray-100 text-gray-600',
}

const ACTIVITY_TYPE_LABEL: Record<Activity['activity_type'], string> = {
  CALL: 'Call', EMAIL: 'Email', MEETING: 'Meeting', NOTE: 'Note', TASK: 'Task',
}

type TabKey = 'details' | 'activities' | 'files'

// ─────────────────── Page ───────────────────
export default function CRMLeadDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const leadId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const leadQ = useQuery({
    queryKey: ['crm', 'lead', leadId],
    queryFn: async () => (await api.get<Lead>(`/crm/leads/${leadId}/`)).data,
    enabled: !!leadId,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<Lead>) =>
      (await api.patch(`/crm/leads/${leadId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'lead', leadId] })
      qc.invalidateQueries({ queryKey: ['crm', 'leads'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/crm/leads/${leadId}/`),
    onSuccess: () => {
      toast.success('Lead deleted')
      qc.invalidateQueries({ queryKey: ['crm', 'leads'] })
      navigate('/admin/crm/leads')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  // Convert drawer state
  const [convertOpen, setConvertOpen] = useState(false)
  const [convertDraft, setConvertDraft] = useState({
    create_opportunity: true,
    opportunity_name: '',
    opportunity_amount: '0',
    opportunity_currency: 'EUR',
  })

  function openConvert(lead: Lead) {
    setConvertDraft({
      create_opportunity: true,
      opportunity_name: lead.company_name
        ? `${lead.company_name} — ${lead.full_name || 'opportunity'}`
        : (lead.full_name || ''),
      opportunity_amount: '0',
      opportunity_currency: 'EUR',
    })
    setConvertOpen(true)
  }

  const convertMut = useMutation({
    mutationFn: async () => (await api.post(`/crm/leads/${leadId}/convert/`, {
      create_opportunity: convertDraft.create_opportunity,
      opportunity_name: convertDraft.opportunity_name,
      opportunity_amount: convertDraft.create_opportunity ? Number(convertDraft.opportunity_amount) : 0,
      opportunity_currency: convertDraft.opportunity_currency,
    })).data,
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ['crm'] })
      toast.success('Lead converted')
      setConvertOpen(false)
      // If an opportunity was created, drop the user on its record page.
      if (data?.opportunity_id) {
        navigate(`/admin/crm/opportunities/${data.opportunity_id}`)
      } else if (data?.account_id) {
        navigate(`/admin/crm/accounts/${data.account_id}`)
      }
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('details')

  if (leadQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (leadQ.isError || !leadQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Lead not found.</p>
          <Link to="/admin/crm/leads" className="text-blue-600 hover:underline">← Back to leads</Link>
        </div>
      </AdminLayout>
    )
  }

  const lead = leadQ.data
  const locked = lead.status === 'CONVERTED'

  const save = (payload: Partial<Lead>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/crm/leads" className="text-muted-foreground hover:text-foreground hover:underline">← Leads</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold text-foreground">{lead.full_name || <span className="italic text-muted-foreground">Unnamed lead</span>}</h2>
            <div className="text-sm text-muted-foreground">
              {lead.job_title && <span>{lead.job_title}</span>}
              {lead.job_title && lead.company_name && <span> @ </span>}
              {lead.company_name && <span>{lead.company_name}</span>}
              {(lead.job_title || lead.company_name) && <span> · </span>}
              <span>{SOURCE_LABEL[lead.source]}</span>
            </div>
            {lead.converted_at && (
              <div className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1">
                <ArrowRight className="h-3 w-3" />
                Converted to{' '}
                {lead.converted_account && (
                  <Link to={`/admin/crm/accounts/${lead.converted_account}`} className="text-blue-600 hover:underline">
                    {lead.converted_account_name}
                  </Link>
                )}
                {lead.converted_opportunity && (
                  <>
                    {' + '}
                    <Link to={`/admin/crm/opportunities/${lead.converted_opportunity}`} className="text-blue-600 hover:underline">
                      {lead.converted_opportunity_name}
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {locked ? (
              <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR.CONVERTED}`}>CONVERTED</span>
            ) : (
              <InlineStatus
                value={lead.status}
                colorMap={STATUS_COLOR}
                options={[
                  { value: 'NEW', label: 'New' },
                  { value: 'CONTACTED', label: 'Contacted' },
                  { value: 'QUALIFIED', label: 'Qualified' },
                  { value: 'UNQUALIFIED', label: 'Unqualified' },
                ]}
                onSave={(v) => save({ status: v })}
              />
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!locked && lead.status !== 'UNQUALIFIED' && (
                  <DropdownMenuItem
                    onClick={() => openConvert(lead)}
                    className="text-green-700"
                  >
                    Convert lead…
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  disabled={locked}
                  onClick={() => !locked && confirm(
                    `lead ${lead.full_name}`,
                    () => deleteMut.mutate(),
                  )}
                  className={!locked ? 'text-destructive' : ''}
                >
                  {locked ? 'Delete (converted)' : 'Delete lead'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['details', 'activities', 'files'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'details' && 'Details'}
              {t === 'activities' && 'Activities'}
              {t === 'files' && 'Files'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'details' && <DetailsTab lead={lead} locked={locked} onSave={save} />}
      {tab === 'activities' && <ActivitiesTab leadId={lead.id} leadName={lead.full_name} />}
      {tab === 'files' && <FilesTab parent="crm.lead" parentId={leadId} />}

      {/* Convert drawer */}
      <RecordDrawer
        open={convertOpen}
        onOpenChange={(v) => { if (!v) setConvertOpen(false) }}
        title="Convert lead"
        description="Creates an Account, a Contact, and optionally an Opportunity. The lead is marked CONVERTED and becomes read-only."
        onSubmit={() => convertMut.mutate()}
        isPending={convertMut.isPending}
        submitLabel="Convert"
      >
        <div className="bg-muted/40 rounded p-3 mb-4 text-sm">
          <div className="font-medium">{lead.full_name}</div>
          {lead.email && <div className="text-xs text-muted-foreground">{lead.email}</div>}
          {lead.company_name && <div className="text-xs text-muted-foreground mt-1">Company: {lead.company_name}</div>}
        </div>

        <FieldGroup>
          <Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={convertDraft.create_opportunity}
                onChange={(e) => setConvertDraft({ ...convertDraft, create_opportunity: e.target.checked })} />
              Create an opportunity from this lead
            </label>
          </Field>

          {convertDraft.create_opportunity && (
            <>
              <Field>
                <FieldLabel htmlFor="conv-opp-name">Opportunity name *</FieldLabel>
                <Input id="conv-opp-name" required value={convertDraft.opportunity_name}
                  onChange={(e) => setConvertDraft({ ...convertDraft, opportunity_name: e.target.value })} />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field>
                  <FieldLabel htmlFor="conv-opp-amount">Amount</FieldLabel>
                  <Input id="conv-opp-amount" type="number" step="0.01"
                    value={convertDraft.opportunity_amount}
                    onChange={(e) => setConvertDraft({ ...convertDraft, opportunity_amount: e.target.value })}
                    className="font-mono text-right" />
                </Field>
                <Field>
                  <FieldLabel htmlFor="conv-opp-currency">Currency</FieldLabel>
                  <Input id="conv-opp-currency" maxLength={3}
                    value={convertDraft.opportunity_currency}
                    onChange={(e) => setConvertDraft({ ...convertDraft, opportunity_currency: e.target.value.toUpperCase() })}
                    className="font-mono" />
                </Field>
              </div>
              <FieldDescription>
                The opportunity is created in the default pipeline's first stage. You can refine pipeline, stage, and contact on the opportunity record page after conversion.
              </FieldDescription>
            </>
          )}
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

// ─────────────────── Details tab ───────────────────

function DetailsTab({
  lead, locked, onSave,
}: {
  lead: Lead
  locked: boolean
  onSave: (payload: Partial<Lead>) => Promise<void>
}) {
  return (
    <div className="space-y-6 max-w-3xl">
      {locked && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
          This lead is <strong>converted</strong> and read-only. Edit downstream records (account, contact, opportunity) instead.
        </div>
      )}

      <Section title="Person">
        <Row label="First name">
          <InlineText value={lead.first_name} onSave={(v) => onSave({ first_name: v })}
            disabled={locked} className={locked ? 'text-muted-foreground' : ''} />
        </Row>
        <Row label="Last name">
          <InlineText value={lead.last_name} onSave={(v) => onSave({ last_name: v })}
            disabled={locked} className={locked ? 'text-muted-foreground' : ''} />
        </Row>
        <Row label="Email">
          <InlineText value={lead.email} onSave={(v) => onSave({ email: v })}
            placeholder="name@company.com" disabled={locked} />
        </Row>
        <Row label="Phone">
          <InlineText value={lead.phone} onSave={(v) => onSave({ phone: v })} disabled={locked} />
        </Row>
        <Row label="Job title">
          <InlineText value={lead.job_title} onSave={(v) => onSave({ job_title: v })} disabled={locked} />
        </Row>
      </Section>

      <Section title="Company">
        <Row label="Company name">
          <InlineText value={lead.company_name} onSave={(v) => onSave({ company_name: v })} disabled={locked} />
        </Row>
        <Row label="Size">
          <InlineSelect
            value={lead.company_size}
            displayLabel={lead.company_size ? SIZE_LABEL[lead.company_size] : <Dash />}
            allowClear
            options={[
              { value: 'SOLO', label: 'SOLO — 1 employee' },
              { value: 'SMALL', label: 'SMALL — 2-50' },
              { value: 'MEDIUM', label: 'MEDIUM — 51-250' },
              { value: 'LARGE', label: 'LARGE — 251-1,000' },
              { value: 'ENTERPRISE', label: 'ENTERPRISE — 1,000+' },
            ]}
            onSave={(v) => onSave({ company_size: v as Lead['company_size'] })}
            disabled={locked}
          />
        </Row>
        <Row label="Industry">
          <InlineText value={lead.industry} onSave={(v) => onSave({ industry: v })} disabled={locked} />
        </Row>
        <Row label="Website">
          <InlineText value={lead.website} onSave={(v) => onSave({ website: v })}
            placeholder="https://…" disabled={locked} />
        </Row>
        <Row label="Country (ISO 2)">
          <InlineText
            value={lead.country_code}
            onSave={(v) => onSave({ country_code: v.toUpperCase().slice(0, 2) })}
            className="font-mono"
            disabled={locked}
          />
        </Row>
      </Section>

      <Section title="Lead data">
        <Row label="Source">
          <InlineSelect
            value={lead.source}
            options={(Object.keys(SOURCE_LABEL) as Lead['source'][]).map((k) => ({
              value: k, label: SOURCE_LABEL[k],
            }))}
            onSave={(v) => onSave({ source: v as Lead['source'] })}
            disabled={locked}
          />
        </Row>
        <Row label="Status">
          {locked ? (
            <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_COLOR.CONVERTED}`}>CONVERTED</span>
          ) : (
            <InlineStatus
              value={lead.status}
              colorMap={STATUS_COLOR}
              options={[
                { value: 'NEW', label: 'New' },
                { value: 'CONTACTED', label: 'Contacted' },
                { value: 'QUALIFIED', label: 'Qualified' },
                { value: 'UNQUALIFIED', label: 'Unqualified' },
              ]}
              onSave={(v) => onSave({ status: v })}
            />
          )}
        </Row>
        <Row label="Score (0-100)">
          <InlineNumber
            value={String(lead.score)}
            step="1"
            onSave={(v) => onSave({ score: Number(v) })}
            displayFormat={(v) => {
              const n = Number(v || 0)
              const color = n >= 70 ? 'bg-red-100 text-red-800'
                : n >= 40 ? 'bg-amber-100 text-amber-800'
                : 'bg-gray-100 text-gray-600'
              return <span className={`inline-block px-2 py-0.5 rounded text-xs font-mono ${color}`}>{n}</span>
            }}
            helper="0-39 cold · 40-69 warm · 70-100 hot."
            disabled={locked}
          />
        </Row>
        <Row label="Owner">
          <span className="text-sm">{lead.owner_username || <Dash />}</span>
        </Row>
      </Section>

      {lead.converted_at && (
        <Section title="Conversion result">
          <Row label="Converted at">
            <span className="text-sm font-mono">{lead.converted_at}</span>
          </Row>
          <Row label="Account">
            {lead.converted_account ? (
              <Link to={`/admin/crm/accounts/${lead.converted_account}`} className="text-blue-600 hover:underline">
                {lead.converted_account_name}
              </Link>
            ) : <Dash />}
          </Row>
          <Row label="Contact">
            <span className="text-sm">{lead.converted_contact_name || <Dash />}</span>
          </Row>
          <Row label="Opportunity">
            {lead.converted_opportunity ? (
              <Link to={`/admin/crm/opportunities/${lead.converted_opportunity}`} className="text-blue-600 hover:underline">
                {lead.converted_opportunity_name}
              </Link>
            ) : <Dash />}
          </Row>
        </Section>
      )}

      <Section title="Notes">
        <Row label="Notes">
          <InlineText
            value={lead.notes}
            multiline
            onSave={(v) => onSave({ notes: v })}
            placeholder="Internal notes about this lead"
            disabled={locked}
          />
        </Row>
      </Section>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-card rounded-lg shadow p-6">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{title}</h3>
      <dl className="grid grid-cols-[180px_1fr] gap-x-6 gap-y-4 text-sm">
        {children}
      </dl>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

// ─────────────────── Activities tab ───────────────────

type ActivityDraft = {
  activity_type: Activity['activity_type']
  subject: string
  description: string
  due_date: string | null
  duration_minutes: number | null
  status: Activity['status']
}

function emptyActivityDraft(): ActivityDraft {
  return {
    activity_type: 'TASK', subject: '', description: '',
    due_date: null, duration_minutes: null, status: 'PENDING',
  }
}

function ActivitiesTab({ leadId, leadName }: { leadId: number; leadName: string }) {
  const qc = useQueryClient()
  const { confirm, dialog } = useDeleteConfirm()
  const activitiesQ = useQuery({
    queryKey: ['crm', 'activities', { lead: leadId }],
    queryFn: async () => (await api.get<Activity[]>(`/crm/activities/?lead=${leadId}`)).data,
  })

  const [editing, setEditing] = useState<Activity | null>(null)
  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<ActivityDraft>(emptyActivityDraft())

  function openCreate() {
    setEditing(null)
    setDraft(emptyActivityDraft())
    setCreating(true)
  }
  function openEdit(a: Activity) {
    setCreating(false); setEditing(a)
    setDraft({
      activity_type: a.activity_type, subject: a.subject, description: a.description,
      due_date: a.due_date, duration_minutes: a.duration_minutes, status: a.status,
    })
  }
  function closeDrawer() { setCreating(false); setEditing(null) }

  const createMut = useMutation({
    mutationFn: async () => (await api.post('/crm/activities/', {
      ...draft,
      related_lead: leadId,
      related_contact: null, related_account: null, related_opportunity: null,
      owner: null,
    })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'activities'] })
      toast.success('Activity added'); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const editMut = useMutation({
    mutationFn: async () => (await api.patch(`/crm/activities/${editing!.id}/`, draft)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'activities'] })
      toast.success('Activity saved'); closeDrawer()
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const inlineMut = useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: Partial<Activity> }) =>
      (await api.patch(`/crm/activities/${id}/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm', 'activities'] }),
  })
  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/crm/activities/${id}/`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['crm', 'activities'] })
      toast.success('Activity deleted')
    },
    onError: (e) => toast.error(extractErr(e)),
  })
  const saveField = async (id: number, payload: Partial<Activity>) => {
    await inlineMut.mutateAsync({ id, payload })
  }

  const activities = activitiesQ.data || []

  return (
    <div className="space-y-4">
      {dialog}
      <div className="flex items-start justify-between">
        <p className="text-xs text-muted-foreground max-w-2xl">
          Calls, emails, meetings, and tasks logged against this lead.
        </p>
        <Button size="sm" onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> Add activity</Button>
      </div>

      {activitiesQ.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : activities.length === 0 ? (
        <p className="text-muted-foreground text-sm">No activities logged yet.</p>
      ) : (
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-muted/40">
            <tr>
              <Th>Type</Th><Th>Subject</Th><Th>Due</Th><Th>Owner</Th><Th>Status</Th><Th />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {activities.map((a) => (
              <tr key={a.id} className={a.is_overdue && a.status === 'PENDING' ? 'bg-amber-50/50' : ''}>
                <Td className="text-xs font-semibold uppercase">{ACTIVITY_TYPE_LABEL[a.activity_type]}</Td>
                <Td className="font-medium">
                  <InlineText value={a.subject} onSave={(v) => saveField(a.id, { subject: v })} />
                </Td>
                <Td className="text-xs">
                  <InlineDate value={a.due_date} onSave={(v) => saveField(a.id, { due_date: v })} />
                  {a.is_overdue && a.status === 'PENDING' && (
                    <div className="text-xs text-amber-700 mt-0.5">overdue</div>
                  )}
                </Td>
                <Td className="text-xs">{a.owner_username || <Dash />}</Td>
                <Td>
                  <InlineStatus
                    value={a.status}
                    colorMap={ACTIVITY_STATUS_COLOR}
                    options={[
                      { value: 'PENDING', label: 'Pending' },
                      { value: 'COMPLETED', label: 'Completed' },
                      { value: 'CANCELLED', label: 'Cancelled' },
                    ]}
                    onSave={(v) => saveField(a.id, { status: v })}
                  />
                </Td>
                <Td className="text-right">
                  <RowActions
                    onEdit={() => openEdit(a)}
                    onDelete={() => confirm(
                      `${ACTIVITY_TYPE_LABEL[a.activity_type]} "${a.subject}"`,
                      () => deleteMut.mutate(a.id),
                    )}
                  />
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <RecordDrawer
        open={creating || editing !== null}
        onOpenChange={(v) => { if (!v) closeDrawer() }}
        title={editing ? 'Edit activity' : 'New activity'}
        description={editing
          ? 'Update this activity.'
          : `Log a call, email, meeting, note, or task against ${leadName}.`}
        onSubmit={() => editing ? editMut.mutate() : createMut.mutate()}
        isPending={createMut.isPending || editMut.isPending}
        submitLabel={editing ? 'Save' : 'Add activity'}
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="ac-type">Type</FieldLabel>
            <Select value={draft.activity_type}
              onValueChange={(v) => setDraft({ ...draft, activity_type: v as Activity['activity_type'] })}>
              <SelectTrigger id="ac-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(ACTIVITY_TYPE_LABEL) as Activity['activity_type'][]).map((k) => (
                  <SelectItem key={k} value={k}>{ACTIVITY_TYPE_LABEL[k]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field>
            <FieldLabel htmlFor="ac-subject">Subject *</FieldLabel>
            <Input id="ac-subject" autoFocus required value={draft.subject}
              onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
          </Field>
          <Field>
            <FieldLabel htmlFor="ac-desc">Description</FieldLabel>
            <Textarea id="ac-desc" rows={3} value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="ac-due">Due date</FieldLabel>
              <DatePicker id="ac-due" value={draft.due_date || ''}
                onChange={(v) => setDraft({ ...draft, due_date: v || null })} />
            </Field>
            <Field>
              <FieldLabel htmlFor="ac-duration">Duration (min)</FieldLabel>
              <Input id="ac-duration" type="number" min="0"
                value={draft.duration_minutes ?? ''}
                onChange={(e) => setDraft({
                  ...draft,
                  duration_minutes: e.target.value === '' ? null : Number(e.target.value),
                })}
                className="font-mono text-right" />
            </Field>
          </div>
          <Field>
            <FieldLabel htmlFor="ac-status">Status</FieldLabel>
            <Select value={draft.status}
              onValueChange={(v) => setDraft({ ...draft, status: v as Activity['status'] })}>
              <SelectTrigger id="ac-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PENDING">Pending</SelectItem>
                <SelectItem value="COMPLETED">Completed</SelectItem>
                <SelectItem value="CANCELLED">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

// ─────────────────── Helpers ───────────────────
function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-foreground ${className}`} onClick={onClick}>{children}</td>
}
